/** Result of running {@link smartFormatHms} on a user-typed string. */
export interface HmsFormatResult {
  /** Best-effort formatted value. When the input parses cleanly, this is
   *  a canonical "HH:MM:SS" string. When the input has digits but is
   *  invalid for some reason (out-of-range parts, negative numbers, more
   *  than three components), this is the best-effort format we managed;
   *  callers usually want to display `error` rather than the value. */
  value: string;
  /** Human-readable error if the input cannot be formatted into a valid
   *  HH:MM:SS string; `null` otherwise. Empty input is treated as valid
   *  (means "no timestamp recorded"). */
  error: string | null;
}

/** Hour upper bound — a sub-rosa segment will never exceed this. */
const MAX_HOURS = 99;

/**
 * Smart-format what the user typed into an "HH:MM:SS" string.
 *
 * Pulls every digit run out of the input and right-anchors them: the last
 * three runs become hours, minutes, seconds (zero-padded). Any non-digit
 * characters act as delimiters, so spaces, colons, slashes, commas, dots,
 * etc. all work.
 *
 * Returns an error message instead of silently truncating or clamping
 * when the input is unambiguously invalid: a negative number, more than
 * three components, hours > 99, or minutes/seconds >= 60.
 *
 * @example
 *   smartFormatHms("1 50")     → { value: "00:01:50", error: null }
 *   smartFormatHms("2 1 50")   → { value: "02:01:50", error: null }
 *   smartFormatHms("1:23")     → { value: "00:01:23", error: null }
 *   smartFormatHms("30")       → { value: "00:00:30", error: null }
 *   smartFormatHms("")         → { value: "",         error: null }
 *   smartFormatHms("1,2,3,4")  → { value: "1,2,3,4",  error: "Too many parts — use HH:MM:SS" }
 *   smartFormatHms("1 60")     → { value: "00:01:60", error: "Minutes and seconds must be 0–59" }
 *   smartFormatHms("100 0 0")  → { value: "100:00:00", error: "Hours must be 0–99" }
 *   smartFormatHms("-5")       → { value: "-5",       error: "Negative numbers are not allowed" }
 */
export function smartFormatHms(raw: string): HmsFormatResult {
  const trimmed = raw.trim();
  if (trimmed === "") return { value: "", error: null };

  // Explicit negative-number reject — `\d+` strips the sign otherwise.
  // Require the `-` to be at the start or preceded by a non-digit so
  // dashes between digits ("12-34-56") count as delimiters, not signs.
  if (/(^|[^0-9])-\d/.test(trimmed)) {
    return { value: raw, error: "Negative numbers are not allowed" };
  }

  const numbers = trimmed.match(/\d+/g);
  if (!numbers || numbers.length === 0) {
    return { value: raw, error: "Enter time as HH:MM:SS" };
  }

  if (numbers.length > 3) {
    return { value: raw, error: "Too many parts — use HH:MM:SS" };
  }

  const padded = numbers.map((n) => n.padStart(2, "0"));
  while (padded.length < 3) padded.unshift("00");
  const value = padded.join(":");

  const [h, m, s] = padded.map((n) => Number(n));
  if (m >= 60 || s >= 60) {
    return { value, error: "Minutes and seconds must be 0–59" };
  }
  if (h > MAX_HOURS) {
    return { value, error: `Hours must be 0–${MAX_HOURS}` };
  }

  return { value, error: null };
}

/**
 * Convenience boolean wrapper around {@link smartFormatHms}. Returns
 * `true` for empty input and any value that formats cleanly into a
 * valid HH:MM:SS string.
 */
export const isValidHHMMSS = (raw: string): boolean =>
  smartFormatHms(raw).error === null;

/**
 * Convert a smart-formatted HH:MM:SS string to total seconds, or
 * `null` if the input is empty / invalid. Used to compare start/end
 * times when both are filled in.
 */
export function hmsToSeconds(raw: string): number | null {
  const result = smartFormatHms(raw);
  if (result.error !== null) return null;
  if (result.value === "") return null;
  const [h, m, s] = result.value.split(":").map((n) => Number(n));
  if ([h, m, s].some((n) => Number.isNaN(n))) return null;
  return h * 3600 + m * 60 + s;
}

/**
 * Validate that an end timestamp is strictly later than a start
 * timestamp. Returns an error message when both are filled in and the
 * end is at or before the start; otherwise `null`. Partial input
 * (either side empty) is treated as valid so the user can fill in
 * one side at a time without seeing a transient error.
 */
export function validateTimeRange(
  startRaw: string,
  endRaw: string,
): string | null {
  const start = hmsToSeconds(startRaw);
  const end = hmsToSeconds(endRaw);
  if (start === null || end === null) return null;
  if (end <= start) return "End time must be after start time";
  return null;
}

/**
 * Format a number of seconds back into a canonical HH:MM:SS string.
 * Fractional seconds are floored. Used for surfacing the actual video
 * duration in error messages.
 */
export function formatSecondsAsHms(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return [h, m, s].map((n) => String(n).padStart(2, "0")).join(":");
}

/**
 * Validate that a timestamp does not exceed the source video's
 * duration. Empty / unparseable input is treated as valid (the
 * per-field formatter surfaces those errors separately).
 */
export function validateTimeWithinDuration(
  raw: string,
  maxDurationSec: number | null | undefined,
): string | null {
  if (maxDurationSec === null || maxDurationSec === undefined) return null;
  const seconds = hmsToSeconds(raw);
  if (seconds === null) return null;
  if (seconds > maxDurationSec) {
    return `Time exceeds video duration (${formatSecondsAsHms(maxDurationSec)})`;
  }
  return null;
}

/**
 * Format a duration in seconds as a human-friendly running label.
 * Below 1 minute → "{Z} sec(s)". Below 1 hour → "{Y} min(s) {Z} sec(s)".
 * 1 hour or more → "{X} h {Y} min(s) {Z} sec(s)". Leading zero units are
 * dropped so short totals stay compact. The "h" unit isn't pluralized
 * because the format uses the bare abbreviation. Fractional seconds
 * round to the nearest whole second.
 */
export function formatHumanDuration(totalSeconds: number): string {
  const total = Math.max(0, Math.round(totalSeconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const sec = seconds === 1 ? "sec" : "secs";
  const min = minutes === 1 ? "min" : "mins";
  if (hours > 0) return `${hours} h ${minutes} ${min} ${seconds} ${sec}`;
  if (minutes > 0) return `${minutes} ${min} ${seconds} ${sec}`;
  return `${seconds} ${sec}`;
}
